import { Injectable, Optional } from "@angular/core";

import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Observable, of, merge, iif, defer } from "rxjs";
import {
  catchError,
  filter,
  map,
  take,
  mergeMap,
  timeout,
  switchMap,
} from "rxjs/operators";
import {
  OAuthErrorEvent,
  OAuthEvent,
  OAuthLogger,
  OAuthResourceServerErrorHandler,
} from "angular-oauth2-oidc";
import { OAuthModuleConfig } from "angular-oauth2-oidc";
import { OAuthService } from "angular-oauth2-oidc";
import { TokenResponse } from "angular-oauth2-oidc";

// Note that this is our custom http interceptor that will try to refresh the token before issuing the http request,
// when it detects that the current access token has expired.
// The token should be refreshed eagerly by a timer but if the refresh failed (because there is no error or the IDP is down),
// then the timer will stop and the service won't try to refresh it any more.
// Note that refreshing the token might also fail (for the same reasons) and you should have a proper error handler that
// can deal with the error (either asking the user or initiating a login for instance).

@Injectable()
export class AutoRefreshOAuthInterceptor implements HttpInterceptor {
  private _pendingRefreshToken: Promise<TokenResponse> | null = null;
  constructor(
    protected oAuthService: OAuthService,
    private errorHandler: OAuthResourceServerErrorHandler,
    protected logger: OAuthLogger,
    @Optional() private moduleConfig: OAuthModuleConfig,
  ) {}

  private checkUrl(url: string): boolean {
    if (this.moduleConfig.resourceServer.customUrlValidation) {
      return this.moduleConfig.resourceServer.customUrlValidation(url);
    }

    if (this.moduleConfig.resourceServer.allowedUrls) {
      return !!this.moduleConfig.resourceServer.allowedUrls.find((u) =>
        url.toLowerCase().startsWith(u.toLowerCase()),
      );
    }

    return true;
  }

  private refreshToken(): Promise<TokenResponse> {
    // Note that we want to protect the IDP from being flooded by refresh token requests
    // if the app triggers a lot of concurrent HTTP calls.
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (this._pendingRefreshToken) {
      return this._pendingRefreshToken;
    }
    this._pendingRefreshToken = this.doRefreshToken()
      .then((val) => {
        this._pendingRefreshToken = null;
        return val;
      })
      .catch((err) => {
        this._pendingRefreshToken = null;
        throw err;
      });
    return this._pendingRefreshToken;
  }

  private async doRefreshToken(): Promise<TokenResponse> {
    try {
      // first, try to do a regular token refresh
      return await this.oAuthService.refreshToken();
    } catch (err) {
      // but if we caught an error implying that the refresh_token is not valid any more,
      // then try a silent refresh instead.
      // Note that the silent refresh might fail if the SSO also expired on the IDP
      if (err instanceof HttpErrorResponse) {
        if (err.status >= 400 && err.status < 500) {
          this.logger.warn(
            `Token refresh failed with a ${err.status} error! Trying a silent refresh instead...`,
          );
          let event: OAuthEvent | undefined = undefined;
          try {
            event = await this.oAuthService.silentRefresh();
            this.logger.debug("event", event);
          } catch (errSilentRefresh) {
            if (errSilentRefresh instanceof OAuthEvent) {
              throw errSilentRefresh;
            } else {
              this.logger.error("silentRefresh failed", errSilentRefresh);
            }
          }
          if (event === undefined || event.type !== "silently_refreshed") {
            throw new HttpErrorResponse({
              status: 401,
              error: "Silent refresh failed!",
            });
          }
          this.logger.info("Silent refresh success!");
          return {
            access_token: this.oAuthService.getAccessToken(),
            id_token: this.oAuthService.getIdToken(),
            token_type: "Bearer",
            expires_in: this.oAuthService.getAccessTokenExpiration(),
            refresh_token: this.oAuthService.getRefreshToken(),
            scope: (
              (this.oAuthService.getGrantedScopes() as string[]) ?? []
            ).join(" "),
          };
        }
      }
      throw err;
    }
  }

  public intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const url = req.url.toLowerCase();

    if (
      !this.moduleConfig ||
      !this.moduleConfig.resourceServer ||
      !this.checkUrl(url)
    ) {
      return next.handle(req);
    }

    const sendAccessToken = this.moduleConfig.resourceServer.sendAccessToken;

    if (!sendAccessToken) {
      return next
        .handle(req)
        .pipe(catchError((err) => this.errorHandler.handleError(err)));
    }
    const isTokenValid = this.oAuthService.hasValidAccessToken();
    const hasRefreshToken = !!this.oAuthService.getRefreshToken();
    const shouldRefreshToken = hasRefreshToken && !isTokenValid;
    const deferredRefreshToken = defer(async () => {
      this.logger.warn(
        "Forcing a token refresh because the current access token is expired!",
      );
      const result = await this.refreshToken();
      this.logger.warn("Received new access token after refresh.");
      return result;
    });
    let hasInvalidRefreshToken = false;

    const refreshTokenStep = iif(
      () => shouldRefreshToken,
      deferredRefreshToken,
      of(null),
    ).pipe(
      catchError((err) => {
        // if the token refresh failed, then try to find out if this is because:
        // - we had an invalid refresh_token
        // - we could not perform a silent refresh because SSO expired and is requesting interactive logon
        // In both cases, it means that we won't be able to authenticate the user with what we have,
        // so, we should force the user to re-authenticate by flushing the session (logout).
        if (err instanceof HttpErrorResponse) {
          if (err.status === 0) {
            this.logger.error(
              "Caught an error while trying to refresh the token because the IDP or the network was down!",
              err,
            );
          } else if (err.status >= 400 && err.status < 500) {
            hasInvalidRefreshToken = true;
            this.logger.error(
              "Caught an error while trying to refresh the token because the current access token is not valid any more!",
              err,
            );
          } else if (err.status >= 500) {
            this.logger.error(
              "Caught an error while trying to refresh the token because the IDP could not process our request",
              err,
            );
          }
        } else if (
          err instanceof OAuthErrorEvent &&
          err.type === "silent_refresh_error" &&
          (err.reason as any).type === "code_error" &&
          isInteractiveLogonError((err.reason as any).params)
        ) {
          hasInvalidRefreshToken = true;
          this.logger.error(
            "Could not do a silent refresh because IDP requires an interactive logon",
          );
        } else {
          this.logger.error("Unknown error", err);
        }
        return of(null);
      }),
    );

    const injectAccessTokenStep = merge(
      defer(() => Promise.resolve(this.oAuthService.getAccessToken())).pipe(
        filter((token) => !!token),
      ),
      this.oAuthService.events.pipe(
        filter((e) => e.type === "token_received"),
        timeout(this.oAuthService.waitForTokenInMsec || 0),
        catchError((_) => of(null)), // timeout is not an error
        map((_) => this.oAuthService.getAccessToken()),
      ),
    ).pipe(
      take(1),
      mergeMap((token) => {
        if (token) {
          const header = "Bearer " + token;
          const headers = req.headers.set("Authorization", header);
          req = req.clone({ headers });
        }

        return next.handle(req).pipe(
          catchError((err) => {
            // if the request failed with a 401 error, it means that our token is invalid (because it could not be refreshed),
            // and we should force the user to logout, in order to let him login again.
            if (
              err instanceof HttpErrorResponse &&
              err.status === 401 &&
              hasRefreshToken &&
              hasInvalidRefreshToken
            ) {
              this.onUnauthorized(err);
            }
            return this.errorHandler.handleError(err);
          }),
        );
      }),
    );

    return refreshTokenStep.pipe(switchMap((_) => injectAccessTokenStep));
  }

  protected onUnauthorized(err: any) {
    // TODO: not sure if we want to force the logout or keep it in order to maintain the claims about the current user.
    // Note however that it doesn't matter when we reload the page, since we would be redirected to the login page of the IDP anyway.
    this.logger.warn(
      "Forcing a logOut since the http call returned a 401, which means that our access_token and refresh_token are not valid any more.",
    );
    this.oAuthService.logOut(true);
    // TODO: if we flushed the session, we might want to redirect the user to the login page or a page explaining that the user has been disconnected...
    // we could also simply reload this page and it would trigger the authentication.
    // Finally, if the app has multiple pages, we should remember the current page and return to it after a successful logon.
    this.logger.warn(
      `Force a page reload since we're not authenticated any more!`,
    );
    window.location.reload(); // or redirect somewhere, or configure auth.config.ts to redirect to a specific endpoint
  }
}

function isInteractiveLogonError(err: any): boolean {
  // Check Gluu error codes
  if (err.error === "interaction_required") {
    return true;
  }
  // Check Azure AD error codes
  if (err.error === "login_required") {
    return true;
  }
  return false;
}
